import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Toast, ToastRegion } from '../components/Toast.js';

export interface ToastRequest {
  message: string;
  tone?: 'default' | 'positive';
  action?: { label: string; onAction: () => void };
}

interface OverlayContextValue {
  showToast: (toast: ToastRequest) => void;
}

const OverlayContext = createContext<OverlayContextValue | null>(null);

/**
 * Dialog, drawer and toast container owned by the design system
 * (07-11 §Scope and Authority). Overlays mount here rather than inside `main`,
 * so a dialog is never nested in the content it interrupts.
 */
export function OverlayHost({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<(ToastRequest & { id: number })[]>([]);

  const showToast = useCallback((toast: ToastRequest) => {
    setToasts((current) => [...current, { ...toast, id: Date.now() + Math.random() }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <OverlayContext.Provider value={value}>
      {children}
      <ToastRegion>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            tone={toast.tone}
            action={toast.action}
            onDismiss={() => dismiss(toast.id)}
          />
        ))}
      </ToastRegion>
    </OverlayContext.Provider>
  );
}

export function useToast(): OverlayContextValue {
  const ctx = useContext(OverlayContext);
  if (!ctx) {
    throw new Error('useToast must be used within an OverlayHost');
  }
  return ctx;
}
